import React, { useState, useEffect, useCallback, useContext } from "react";
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from "react-router-dom";
import { LoginContext } from "../../../../services/api/authentication-api";
import NoPermissionsPanel from "../../../../components/no-permissions-panel/no-permissions-panel";
import NewProjectStatusChangePopupNew from "../new-project-status-change-popup/new-project-status-change-popup.jsx";
import { getAccessor, handleViewGrouping } from "../../../../util/grouping-helpers";
import ProjectsTableNew from "../../../../components/expandable-table/table/expandable-table-new.jsx";
import ExpandableFilterRowNew from "../../../../components/expandable-table/expandable-filter-row/expandable-filter-row-new.jsx";
import ButtonWrapperNew from "../../../../components/expandable-table/quick-action-buttons/quick-action-buttons.jsx";
import ProjectsRowNew from "../projects-row/ProjectsRowNew";
import { errorNotification, successNotification } from "../../../../redux/notifications";
import {
  getProjectsNew,
  getProjectsGroupedNew,
  getProjectsCount,
  getProjectsUsersAndGroups,
  addMultipleSelectedProjects,
  deleteMultipleSelectedProjects,
} from "../../../../redux/projects/project-actions";
import {
  updateProjectSortSelection,
  updateProjectSortOrder,
  clearProjectSort,
} from "../../../../redux/sort/sort-actions";
import {
  updateProjectFilterOptionsTotal,
  updateProjectFiltersApplied,
  updateProjectFiltersConjunctionSelection,
} from "../../../../redux/filters/filter-actions";
import { selectUser, selectUserID } from '../../../../redux/user/user-selector';
import {
  selectProjects,
  selectProjectsUsers,
  selectProjectsGroups,
  selectProjectsCount,
  selectIsFetchingProjects,
  selectSelectedProjects,
  selectGroupProjectsBy,
  selectFilteredProjectsSorted,
} from "../../../../redux/projects/project-selector";
import { selectProjectFilters, selectProjectFiltersApplied } from '../../../../redux/filters/filter-selector';
import { selectProjectSort } from '../../../../redux/sort/sort-selector';
import "./project-table-page.scss";

const getProjectSortOptionsItems = (labels) => [
  {
    name: `${labels?.ProjectSingular ?? "Project"} names`,
    id: 0,
    projectField: "Name",
  },
  {
    name: "Priority",
    id: 1,
    projectField: "Priority",
  },
  {
    name: "Start date",
    id: 2,
    projectField: "Start",
  },
  {
    name: "Due date",
    id: 3,
    projectField: "End",
  },
  {
    name: `Number of ${labels?.TaskPlural ?? "tasks"}`,
    id: 4,
    projectField: "TasksTotalCount",
  },
];

const ProjectTablePageNew = ({
  privileges,
  dateFormatID,
}) => {
  const loggedInUser = useContext(LoginContext);
  const labels = loggedInUser?.currentUser?.objectLabels;
  const [showProjectStatusChangePopup, setShowProjectStatusChangePopup] = useState(false);
  const [showNoPermissions, setshowNoPermissions] = useState(false);
  const [noPermissionsMessage, setnoPermissionsMessage] = useState("You don't have permission to view this tab");
  const [showGroupBy, setShowGroupBy] = useState(false);
  const [projectsToEdit, setProjectsToEdit] = useState([]);
  const [actionObj, setActionObj] = useState({});
  const [isInitialRender, SetIsInitialRender] = useState(true);
  const [initialRenderCompleted, setInitialRenderCompleted] = useState(false);
  const user = useSelector(selectUser);
  const userID = useSelector(selectUserID);
  const projects = useSelector(selectProjects);
  const projectsUsers = useSelector(selectProjectsUsers);
  const projectsGroups = useSelector(selectProjectsGroups);
  const isFetchingProjects = useSelector(selectIsFetchingProjects);
  const selectedProjects = useSelector(selectSelectedProjects);
  const groupBy = useSelector(selectGroupProjectsBy);
  const projectFilterData = useSelector(selectProjectFilters);
  const projectFiltersApplied = useSelector(selectProjectFiltersApplied);
  const projectSort = useSelector(selectProjectSort);
  const sortedFilteredProjects = useSelector(selectFilteredProjectsSorted(labels));
  const projectsCount = useSelector(selectProjectsCount);

  const dispatch = useDispatch();
  const getProjectsCountTablePage = () => dispatch(getProjectsCount());
  const getProjectsUsersAndGroupTablePage = () => dispatch(getProjectsUsersAndGroups());
  const getProjectsTablePage = (projectQuery) => dispatch(getProjectsNew(projectQuery));
  const getProjectsGroupedTablePage = (groupBy, projectQuery) => dispatch(getProjectsGroupedNew(groupBy, projectQuery,labels));
  const updateProjectFilterOptionsTotalTablePage = (totalProjectFilters) => dispatch(updateProjectFilterOptionsTotal(totalProjectFilters));
  const updateAppliedProjectFilters = (projectFiltersApplied) => dispatch(updateProjectFiltersApplied(projectFiltersApplied));
  const updateProjectFilterConjunctionSelection = (projectFilterConjunctionSelection) => dispatch(updateProjectFiltersConjunctionSelection(projectFilterConjunctionSelection));
  const setProjectSortSelection = (sortSelection) => dispatch(updateProjectSortSelection(sortSelection));
  const setProjectSortOrder = (sortOrder) => dispatch(updateProjectSortOrder(sortOrder));
  const setClearProjectSort = () => dispatch(clearProjectSort());
  const addMultipleSelectedProjectsTablePage = (projIdList) => dispatch(addMultipleSelectedProjects(projIdList));
  const deleteMultipleSelectedProjectsTablePage = (projIdList) => dispatch(deleteMultipleSelectedProjects(projIdList));
  const isMultiOwner = true;
 
  // State for 4 action buttons (dependent on selected projects)
  const [buttonState, setButtonState] = useState({
    complete: false,
    reopen: false,
    archive: false,
    unarchive: false,
  });

  // group by Category
  const [category, setCategory] = useState({
    showActive: false,
    showCompleted: false,
    showArchived: false,
    viewAllActive: false,
    viewAllArchived: false,
    viewAllCompleted: false,
  });

  // group by Priority
  const [priority, setPriority] = useState({
    showUrgent: false,
    showHigh: false,
    showMedium: false,
    showLow: false,
    viewAllUrgent: false,
    viewAllHigh: false,
    viewAllMedium: false,
    viewAllLow: false,
  });

  useEffect(() => {
    SetIsInitialRender(true);
    getProjectsCountTablePage();
    getProjectsUsersAndGroupTablePage();
    refreshTable();
    setInitialRenderCompleted(true);
  }, []);

  const onEdit = (isError, message, projName = "", toasterMessage = "") => {
    let projIdentifier = `The ${labels?.ProjectSingular ?? "Project"} "${projName}" has`;

    // Display "X projects" or "projectName"
    if (projectsToEdit.length > 1) {
      projIdentifier = `${projectsToEdit.length} ${labels?.ProjectPlural ?? "projects"} have`;
    }

    if (isError) {
      const notification = {
          header: `Edit ${labels?.ProjectSingular ?? "Project"}`,
          message: message === "noPermissionToAddMembers"
              ? `You don't have permission to add members to this ${labels?.ProjectSingular ?? "project"}`
              : `${projIdentifier} failed to ${toasterMessage}`,
      };
      dispatch(errorNotification(notification));
    } else {
      let header;
      let toastMessage;

      switch (toasterMessage) {
        case " reopen":
          header = `${labels?.ProjectSingular ?? "Project"} Re-Opened`;
          toastMessage = projIdentifier + " been Re-Opened successfully";
          break;
        case " complete":
          header = `${labels?.ProjectSingular ?? "Project"} Completed`;
          toastMessage = projIdentifier + " been Completed successfully";
          break;
        case " archive":
          header = `${labels?.ProjectSingular ?? "Project"} Archived`;
          toastMessage = projIdentifier + " been Archived successfully";
          break;
        case " unarchive":
          header = `${labels?.ProjectSingular ?? "Project"} Un-Archived`;
          toastMessage = projIdentifier + " been Un-Archived successfully";
          break;
        default:
          header = `${labels?.ProjectSingular ?? "Project"} Edited`;
          toastMessage = projIdentifier + " been Updated successfully";
      }
      refreshTable();
      dispatch(successNotification({
        header: header,
        message: toastMessage,
      }));
    }
  };

  const handlePermissions = (message) => {
    setshowNoPermissions(true);
    setnoPermissionsMessage(message);
  };

  /*
      type: the sub grouping
      payload: the value to set
      action: the type of action (set or toggle?)
  */
  const callback = ({ type, payload, actionType }) => {
    const subgroup = type.split("_")[1];

    if (!subgroup) return;

    let accessor = getAccessor(type);

    if (
      subgroup === "active" ||
      subgroup === "archived" ||
      subgroup === "completed"
    ) {
      setCategory((prev) =>
        handleViewGrouping(prev, accessor, payload, actionType)
      );
    } else if (
      subgroup === "urgent" ||
      subgroup === "high" ||
      subgroup === "medium" ||
      subgroup === "low"
    ) {
      setPriority((prev) =>
        handleViewGrouping(prev, accessor, payload, actionType)
      );
    }
  };

  const buttonCallback = ({ type, payload }) => {
    if (
      type === "complete" ||
      type === "reopen" ||
      type === "unarchive" ||
      type === "archive"
    ) {
      setButtonState((prev) => {
        let copy = { ...prev };
        copy[type] = payload;

        return copy;
      });
    } else if (type === "actionObj") setActionObj(payload);
    else if (type === "projectsToEdit") setProjectsToEdit(payload);
    else if (type === "showPopup") setShowProjectStatusChangePopup(payload);
  };

  const keyFunction = (project) => {
    return project.Id;
  };

  // Handles clicking the x-icon that appears in blue showing the number of filters currently being applied
  const clearProjectsFilter = () => {
    const newTotalFilterOptions = projectFilterData.totalFilterOptions.map(
      (filterOption) => {
        return { ...filterOption, isAdded: false };
      }
    );

    updateProjectFilterOptionsTotalTablePage(newTotalFilterOptions);
    updateAppliedProjectFilters([]);
  };

  // Refreshes Group By, minimizes accordions
  const refreshTable = useCallback(() => {
    // Only call this if we are NOT already fetching
    if (isFetchingProjects && !isInitialRender) {
      return;
    }

    // If not fetching, go ahead and call
    getProjectsGroupedTablePage(groupBy);

    if (isInitialRender) SetIsInitialRender(false);

    // Reset "view all" toggles after fetch
    setCategory((prev) => ({
      ...prev,
      viewAllActive: false,
      viewAllCompleted: false,
      viewAllArchived: false,
    }));
    setPriority((prev) => ({
      ...prev,
      viewAllUrgent: false,
      viewAllHigh: false,
      viewAllMedium: false,
      viewAllLow: false,
    }));
  }, [
    isFetchingProjects,
    getProjectsGroupedTablePage,
    groupBy,
    setCategory,
    setPriority,
  ]);

  //project permissions
  const CanEdit = (project) => {
    let isUserAMember = project.Members?.filter((member) => member.UserId === userID).length > 0;
    let isUserAGroupMember = project.Groups?.some(
      (group) => group.Group?.Members?.some((member) => member.UserId === userID)
    );
    //legacy project groups:
    let isUserAGroupMemberInMembers = project.Members?.some(
      (member) => member.IsGroup && member.Group?.Members?.some((m) => m.UserId === userID)
    );

    if (project.CreatedById === userID || user.profile.isSuperAdminRole || isUserAMember || isUserAGroupMember || isUserAGroupMemberInMembers)
      return true;

    if (!isMultiOwner)
      return project.OwnerId === userID;
  
    return false;
  }

  const TableRow = (props) => (
    <ProjectsRowNew
      project={props.data}
      projectsUsers={projectsUsers}
      projectsGroups={projectsGroups}
      onEdit={onEdit}
      handlePermissions={handlePermissions}
      refreshTable={refreshTable}
      tab={"active"}
      taskCreateDisabled={
        privileges &&
        privileges["Task Management"] &&
        !privileges["Task Management"].Add
      }
      // The add member functionality is based upon whether or not they are a super admin or owner of the project or creator of the project or member of the project
      memberAddDisabled={
        CanEdit(props.data)
      }
      isMultiOwner={isMultiOwner}
      labels={labels}
    />
  );

  return (
    <LoginContext.Consumer>
      {() => (
        <div className="ProjectsPage dashboard-container-shadow">
          { showNoPermissions &&
            <NoPermissionsPanel
              show={showNoPermissions}
              onHide={() => setshowNoPermissions(false)}
              message={noPermissionsMessage}
            />
          }
          { initialRenderCompleted &&
            <div className="projects-table-container">
              <ExpandableFilterRowNew
                dateFormatID={dateFormatID}
                groupBy={groupBy}
                itemFilterData={projectFilterData}
                dataSort={projectSort}
                getDataGrouped={getProjectsGroupedTablePage}
                updateDataFilterOptionsTotal={updateProjectFilterOptionsTotalTablePage}
                updateAppliedDataFilters={updateAppliedProjectFilters}
                updateDataFilterConjunctionSelection={updateProjectFilterConjunctionSelection}
                setDataSortSelection={setProjectSortSelection}
                setDataSortOrder={setProjectSortOrder}
                clearDataFilters={clearProjectsFilter}
                showGroupBy={showGroupBy}
                disabled={
                  privileges &&
                  privileges["Projects"] &&
                  !privileges["Projects"].Add
                }
                setShowGroupBy={setShowGroupBy}
                clearDataSort={setClearProjectSort}
                refreshTable={refreshTable}
                totalSortOptions={getProjectSortOptionsItems(labels)}
                sortHeaderText={`Sort ${labels?.ProjectPlural ?? "Projects"}`}
                pageIdentifier={"project"}
                labels={labels}
              >
                <ButtonWrapperNew
                  data={projects}
                  selectedData={selectedProjects}
                  callback={buttonCallback}
                  buttonState={buttonState}
                  canEdit={CanEdit}
                />
              </ExpandableFilterRowNew>
              <div className="projects-table">
                <NewProjectStatusChangePopupNew
                  new={true}
                  show={showProjectStatusChangePopup}
                  onHide={() => setShowProjectStatusChangePopup(false)}
                  type={actionObj.actiontype}
                  status={actionObj.status}
                  button="Confirm"
                  onEdit={onEdit}
                  projects={projectsToEdit}
                  refreshTable={refreshTable}
                  popupLabel={
                    actionObj.actiontype == "Active" ? actionObj.popupLabel : ""
                  }
                  labels={labels}
                />
                <ProjectsTableNew
                  data={projects}
                  projectsUsers={projectsUsers}
                  projectsGroups={projectsGroups}
                  isFetchingData={isFetchingProjects}
                  selectedData={selectedProjects}
                  groupBy={groupBy}
                  filtersApplied={projectFiltersApplied}
                  sortedFilteredData={sortedFilteredProjects}
                  addMultipleSelectedDataItems={addMultipleSelectedProjectsTablePage}
                  deleteMultipleSelectedDataItems={deleteMultipleSelectedProjectsTablePage}
                  keyFunction={keyFunction}
                  TableRow={TableRow}
                  callback={callback}
                  tableDataGroups={{
                    priority: [
                      {
                        type: "urgent",
                        state: priority.showUrgent,
                        viewAll: priority.viewAllUrgent,
                      },
                      {
                        type: "high",
                        state: priority.showHigh,
                        viewAll: priority.viewAllHigh,
                      },
                      {
                        type: "medium",
                        state: priority.showMedium,
                        viewAll: priority.viewAllMedium,
                      },
                      {
                        type: "low",
                        state: priority.showLow,
                        viewAll: priority.viewAllLow,
                      },
                    ],
                    status: [
                      {
                        type: "active",
                        state: category.showActive,
                        viewAll: category.viewAllActive,
                      },
                      {
                        type: "completed",
                        state: category.showCompleted,
                        viewAll: category.viewAllCompleted,
                      },
                      {
                        type: "archived",
                        state: category.showArchived,
                        viewAll: category.viewAllArchived,
                      },
                    ],
                    none: [{ type: "none", state: true, viewAll: false }],
                  }}
                  pageIdentifier={"project"}
                  clearProjectsFilter={clearProjectsFilter}
                  refreshTable={refreshTable}
                  projectsCount={projectsCount}
                  labels={labels}
                />
              </div>
            </div>
          }
        </div>
      )}
    </LoginContext.Consumer>
  );
};

ProjectTablePageNew.propTypes = {
  dateFormatID: PropTypes.string,
  privileges: PropTypes.object
};

ProjectTablePageNew.contextType = LoginContext;

export default withRouter(ProjectTablePageNew);
